import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { useClientTranslation } from '@/lib/core/i18n';
import { cn } from '@/lib/core';
import type { ProductVariantDto } from '@/services/platform/api/model';

interface ChooseSizeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variants: ProductVariantDto[];
  onSelect: (variant: ProductVariantDto) => void;
  onOpenSizeChart?: () => void;
}

/**
 * Bottom sheet listing the available sizes for the product. Sizes with no
 * stock (`variant.stock <= 0`) render greyed out with a "SOLD OUT" pill and
 * are not selectable.
 */
export function ChooseSizeSheet({
  open,
  onOpenChange,
  variants,
  onSelect,
  onOpenSizeChart,
}: ChooseSizeSheetProps) {
  const t = useClientTranslation();

  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
      <DrawerContent className="max-h-[80vh]">
        <div data-base-ui-swipe-ignore className="flex flex-1 flex-col overflow-y-auto">
          <div className="flex flex-col px-4 pt-4">
            <h2 className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] leading-none font-normal tracking-[0.5px] uppercase">
              {t('App.Shop.ChooseSize')}
            </h2>
          </div>

          <ul className="mt-4 flex flex-col">
            {variants.map(variant => {
              const soldOut = (variant.stock ?? 0) <= 0;
              const label = variant.option1 ?? variant.title ?? '—';
              return (
                <li key={variant.publicGuid ?? label}>
                  <button
                    type="button"
                    disabled={soldOut}
                    onClick={() => {
                      if (soldOut) return;
                      onSelect(variant);
                    }}
                    className={cn(
                      'border-jncquoi-neutral-200 relative flex h-12 w-full items-center px-4',
                      soldOut
                        ? 'text-jncquoi-neutral-400 cursor-not-allowed'
                        : 'text-jncquoi-neutral-1000 hover:bg-jncquoi-neutral-50 active:bg-jncquoi-neutral-100 cursor-pointer'
                    )}
                  >
                    <span className="font-pp-neue-montreal pointer-events-none absolute inset-0 flex items-center justify-center text-[16px] leading-none font-normal">
                      {label}
                    </span>
                    {soldOut && (
                      <span className="border-jncquoi-neutral-400 text-jncquoi-neutral-600 font-pp-neue-montreal ml-auto border px-2 py-1 text-[10px] leading-none tracking-[0.5px] uppercase">
                        {t('App.Shop.SoldOut')}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {onOpenSizeChart && (
            <div className="border-jncquoi-neutral-200 shrink-0 px-4 pt-4 pb-10.5">
              <button
                type="button"
                onClick={onOpenSizeChart}
                className="text-jncquoi-neutral-1000 font-pp-neue-montreal w-full cursor-pointer text-center text-[12px] leading-none font-normal tracking-[0.5px] uppercase"
              >
                {t('App.Shop.SizeChart')}
              </button>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
